/* eslint-disable react/prop-types */

import React, { useState, useRef } from 'react';
import { Form, Col } from 'react-bootstrap';
import { FormLabel } from './FormLabel';
import { FaRegCalendar } from 'react-icons/fa';
import dayjs from 'dayjs';

const formatToDDMonYYYY = date => {
  return date ? dayjs(date).format('DD-MMM-YYYY') : '';
};

const Dob = ({
  metadata,
  field,
  setValue,
  register,
  errors,
  ErrorFeedback
}) => {
  const [selectedDate, setSelectedDate] = useState(() => {
    const val = metadata.defaultValue;
    if (!val) return null;
    if (val === 'NOW') return new Date();
    const date = new Date(val);
    return isNaN(date.getTime()) ? null : date;
  });

  // Sync "NOW" default value to form state
  React.useEffect(() => {
    if (
      metadata.defaultValue === 'NOW' &&
      selectedDate &&
      !isNaN(selectedDate.getTime()) &&
      setValue
    ) {
      setValue(metadata.id, selectedDate.toISOString().split('T')[0]);
    }
  }, [metadata.defaultValue, metadata.id, selectedDate, setValue]);

  const hiddenInputRef = useRef(null);

  // Function to calculate date with offset

  const calculateDateWithOffset = (days, isSubtract = false) => {
    const date = new Date();
    const offsetMilliseconds = days * 24 * 60 * 60 * 1000;

    if (isSubtract) {
      date.setTime(date.getTime() - offsetMilliseconds);
    } else {
      date.setTime(date.getTime() + offsetMilliseconds);
    }

    return date.toISOString().split('T')[0];
  };

  // Calculate min and max dates based on validation
  const calculateDateRange = () => {
    const today = new Date().toISOString().split('T')[0];
    let minDate = null;
    let maxDate = null;

    if (metadata?.validation) {
      // Handle past days restriction
      if (
        metadata.validation.noOfPastDays ||
        metadata.validation.noOfPastDays === 0
      ) {
        minDate = calculateDateWithOffset(
          metadata.validation.noOfPastDays,
          true
        );
      }

      // Handle future days restriction
      if (
        metadata.validation.noOfFutureDays ||
        metadata.validation.noOfFutureDays === 0
      ) {
        maxDate = calculateDateWithOffset(metadata.validation.noOfFutureDays);
      }

      // Special handling for DOB type
      if (metadata.type === 'dob') {
        const eighteenYearsAgo = new Date();
        eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);
        maxDate = eighteenYearsAgo.toISOString().split('T')[0];
      }

      // Handle noFutureDate validation
      if (metadata.validation.noFutureDate) {
        maxDate = today;
      }
    }

    return { minDate, maxDate };
  };

  const { minDate, maxDate } = calculateDateRange();

  // Handle date change
  const handleDateChange = e => {
    const newDate = e.target.value;
    setSelectedDate(newDate ? new Date(newDate) : null);
    if (setValue) {
      setValue(metadata.id, newDate);
    }
  };

  // Format default value to YYYY-MM-DD
  const formatDefaultValue = value => {
    if (!value) return '';
    // If it's a date string with time, extract just the date
    if (value.includes('T')) {
      return value.split('T')[0];
    }
    return value;
  };

  return (
    <Col>
      <Form.Group as={Col} sm={12} md={12} className="mb-3">
        <FormLabel
          label={metadata.label}
          required={metadata?.validation?.required}
        />
        <div style={{ position: 'relative' }}>
          {/* Visible formatted date */}
          <div style={{ position: 'relative' }}>
            {/* Read-only, user-visible formatted date */}
            <Form.Control
              type="text"
              value={selectedDate ? formatToDDMonYYYY(selectedDate) : ''}
              readOnly
              placeholder="DD-Mon-YYYY"
              style={{
                backgroundColor: '#fff',
                paddingRight: '2.5rem',
                zIndex: 1
              }}
              onClick={() => hiddenInputRef.current?.showPicker()}
            />
            {/* Manual calendar icon */}
            <div
              onClick={() => hiddenInputRef.current?.showPicker()}
              style={{
                position: 'absolute',
                right: '1rem',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 3
              }}
            >
              <FaRegCalendar style={{ fontSize: '0.9rem' }} />
            </div>
            {/* Native calendar picker (invisible but clickable) */}
            <input
              type="date"
              ref={hiddenInputRef}
              min={minDate}
              max={maxDate}
              onChange={handleDateChange}
              value={
                selectedDate && !isNaN(selectedDate.getTime())
                  ? selectedDate.toISOString().split('T')[0]
                  : formatDefaultValue(metadata.defaultValue)
              }
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: 0, // hide text
                zIndex: 2,
                border: 'none'
              }}
            />
          </div>

          <ErrorFeedback error={errors[metadata.id]} />
        </div>
      </Form.Group>
    </Col>
  );
};

export default Dob;
